<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { useMapGetter, useStoreGetters } from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { getChannelSvgUrlForInbox } from 'dashboard/components-next/icon/channelSvgUrls';
import { getCatalogCardGradientClassForInbox } from 'dashboard/helper/inboxCatalogCardStyle';

const getters = useStoreGetters();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const searchQuery = ref('');

const inboxes = useMapGetter('inboxes/getInboxes');

const inboxesList = computed(() => {
  return inboxes.value?.slice().sort((a, b) => a.name.localeCompare(b.name));
});

const filteredInboxesList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return inboxesList.value;
  return picoSearch(inboxesList.value, query, ['name', 'channel_type']);
});

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const channelsEmptyDescription = computed(() =>
  isAdmin.value
    ? t('INBOX_MGMT.EMPTY_STATE.DESCRIPTION_ADMIN', {
        cta: t('SETTINGS.INBOXES.NEW_INBOX'),
      })
    : t('INBOX_MGMT.EMPTY_STATE.DESCRIPTION_NON_ADMIN')
);

const channelSvgUrl = inbox => getChannelSvgUrlForInbox(inbox) || '';

const inboxCardGradientClass = inbox =>
  getCatalogCardGradientClassForInbox(inbox);
</script>

<template>
  <SettingsLayout :is-loading="uiFlags.isFetching">
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INBOX_MGMT.CHANNELS_PAGE_HEADER')"
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        :search-placeholder="$t('INBOX_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="inboxes"
      >
        <template v-if="inboxesList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('INBOX_MGMT.COUNT', { n: inboxesList.length }) }}
          </span>
        </template>
        <template #actions>
          <router-link v-if="isAdmin" :to="{ name: 'settings_inbox_new' }">
            <Button
              icon="i-lucide-plus"
              :label="$t('SETTINGS.INBOXES.NEW_INBOX')"
              size="sm"
            />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div
        v-if="!inboxesList.length"
        class="flex min-h-[22rem] flex-col items-center justify-center px-0 py-16 text-center"
      >
        <div
          class="mb-6 flex size-24 items-center justify-center rounded-full bg-n-alpha-2 outline outline-1 outline-n-weak -outline-offset-1"
        >
          <Icon icon="i-lucide-layers-2" class="size-12 text-n-slate-11" />
        </div>
        <h3 class="mb-2 text-lg font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.EMPTY_STATE.TITLE') }}
        </h3>
        <p class="max-w-md text-body-main text-n-slate-11">
          {{ channelsEmptyDescription }}
        </p>
      </div>

      <span
        v-else-if="!filteredInboxesList.length && searchQuery"
        class="flex flex-1 items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.NO_RESULTS') }}
      </span>
      <div
        v-else
        class="grid grid-cols-1 gap-4 pb-6 pt-1 md:grid-cols-2 lg:grid-cols-4"
      >
        <article
          v-for="inbox in filteredInboxesList"
          :key="inbox.id"
          class="relative isolate flex min-w-0 flex-col overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 shadow-sm"
        >
          <div
            class="pointer-events-none absolute inset-0 rounded-lg"
            :class="inboxCardGradientClass(inbox)"
            aria-hidden="true"
          />
          <div
            class="relative z-10 flex min-h-[7rem] items-start justify-between gap-3 px-4 py-4"
          >
            <div class="flex min-w-0 flex-1 flex-col gap-1">
              <span
                class="block truncate text-start text-lg font-bold capitalize text-n-slate-12"
              >
                {{ inbox.name }}
              </span>
              <p
                class="mb-0 text-start text-sm font-normal leading-snug text-n-slate-11"
              >
                <ChannelName
                  :channel-type="inbox.channel_type"
                  :medium="inbox.medium"
                  :provider="inbox.provider"
                  class="inline"
                />
                <span>{{
                  $t('INBOX_MGMT.LIST_CARDS.ID_SUFFIX', { id: inbox.id })
                }}</span>
              </p>
            </div>
            <div class="flex shrink-0 items-center">
              <img
                v-if="channelSvgUrl(inbox)"
                :src="channelSvgUrl(inbox)"
                alt=""
                decoding="async"
                draggable="false"
                class="size-10 shrink-0 object-contain"
              />
              <ChannelIcon
                v-else
                class="size-10 text-n-slate-10"
                :inbox="inbox"
              />
            </div>
          </div>
          <div class="relative z-10 px-4">
            <div
              class="h-px shrink-0 bg-n-weak/90 dark:bg-n-strong/50"
              role="presentation"
              aria-hidden="true"
            />
          </div>
          <div
            class="relative z-10 flex min-h-[3.5rem] items-center justify-between gap-3 px-4 py-3"
            role="group"
          >
            <Icon
              icon="i-lucide-inbox"
              class="size-5 shrink-0 text-n-slate-10"
              aria-hidden="true"
            />
            <router-link
              v-if="isAdmin"
              :to="{
                name: 'settings_inbox_show',
                params: { inboxId: inbox.id },
              }"
              class="inline-flex shrink-0"
            >
              <Button
                class="!font-bold"
                outline
                slate
                sm
                :label="$t('INBOX_MGMT.LIST_CARDS.MANAGE')"
              />
            </router-link>
          </div>
        </article>

        <article
          v-if="isAdmin && filteredInboxesList.length"
          class="relative isolate flex min-w-0 flex-col overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 shadow-sm"
        >
          <div
            class="relative z-10 flex min-h-[7rem] flex-col items-center justify-center px-4 py-4 text-center"
          >
            <p class="mb-0 max-w-sm text-body-main text-n-slate-11">
              {{ $t('INBOX_MGMT.LIST_CARDS.CTA_BODY') }}
            </p>
          </div>
          <div class="relative z-10 px-4">
            <div
              class="h-px shrink-0 bg-n-weak/90 dark:bg-n-strong/50"
              role="presentation"
              aria-hidden="true"
            />
          </div>
          <div
            class="relative z-10 flex min-h-[3.5rem] items-center justify-between gap-3 px-4 py-3"
          >
            <span class="size-5 shrink-0" aria-hidden="true" />
            <router-link
              :to="{ name: 'settings_inbox_new' }"
              class="inline-flex shrink-0"
            >
              <Button
                outline
                slate
                sm
                :label="$t('INBOX_MGMT.LIST_CARDS.BROWSE_CATALOG')"
              />
            </router-link>
          </div>
        </article>
      </div>
    </template>
  </SettingsLayout>
</template>
